import { useEffect, useState } from 'react';
import type { Tarea, TareaDetalle, UsuarioEquipo } from '@matias-asset-os/contracts';
import { t, type Idioma } from '@matias-asset-os/i18n';
import { api, ErrorApi } from '../api.js';
import { Icono } from './Iconos.js';
import { GaleriaFotos } from './GaleriaFotos.js';
import { fechaLarga } from '../componentes.js';

/**
 * Una tarea abierta, en un panel a la derecha.
 *
 * Es el gesto de Asana y se conserva a propósito: abrir una tarea no puede
 * costar perder de vista la lista, porque lo que se está haciendo casi siempre
 * es comparar unas con otras.
 *
 * El panel guarda EN CUANTO se cambia algo, sin botón. Un panel que se cierra
 * al pulsar fuera y además exige guardar es una forma segura de perder trabajo.
 */

interface Props {
  tareaId: string;
  idioma: Idioma;
  equipo: UsuarioEquipo[];
  onCerrar: () => void;
  /** Se avisa con la tarea ya guardada para que la lista se actualice sin recargar. */
  onCambio: (tarea: Tarea) => void;
}

const iniciales = (nombre: string) =>
  nombre
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p.charAt(0).toUpperCase())
    .join('');

export function PanelTarea({ idioma, tareaId, equipo, onCerrar, onCambio }: Props) {
  const [tarea, setTarea] = useState<TareaDetalle | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [comentario, setComentario] = useState('');
  const [nuevaSub, setNuevaSub] = useState('');
  const [nuevaEtiqueta, setNuevaEtiqueta] = useState('');
  const [eligiendoGente, setEligiendoGente] = useState(false);

  const cargar = () =>
    api
      .proyAbrirTarea(tareaId)
      .then((r) => setTarea(r.tarea))
      .catch((e: unknown) =>
        setError(e instanceof ErrorApi ? e.message : t(idioma, 'tarea.noSeAbrio')),
      );

  useEffect(() => {
    setTarea(null);
    setError(null);
    void cargar();
    // `cargar` se redefine en cada render; anadirla aqui volveria a pedir la
    // tarea sin parar. Lo que decide es el identificador, y ese esta.
  }, [tareaId]);

  // La tecla de escape cierra, como en cualquier panel del sistema.
  useEffect(() => {
    const alPulsar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCerrar();
    };
    window.addEventListener('keydown', alPulsar);
    return () => window.removeEventListener('keydown', alPulsar);
  }, [onCerrar]);

  if (error && !tarea) {
    return (
      <aside className="panel-tarea">
        <p className="error" role="alert">
          {error}
        </p>
        <button type="button" className="secundario" onClick={onCerrar}>
          Cerrar
        </button>
      </aside>
    );
  }
  if (!tarea) {
    return (
      <aside className="panel-tarea">
        <p className="suave">{t(idioma, 'app.cargando')}</p>
      </aside>
    );
  }

  const guardar = (cambios: Parameters<typeof api.proyEditarTarea>[1]) => {
    setError(null);
    api
      .proyEditarTarea(tarea.id, cambios)
      .then(({ tarea: actualizada }) => {
        setTarea((previa) => (previa ? { ...previa, ...actualizada } : previa));
        onCambio(actualizada);
      })
      .catch((e: unknown) =>
        setError(e instanceof ErrorApi ? e.message : t(idioma, 'tarea.noSeGuardo')),
      );
  };

  const responsables = tarea.responsables.filter((p) => p.rol === 'RESPONSABLE');
  const hecha = tarea.estado === 'HECHA';
  const idsResponsables = responsables.map((p) => p.userId);

  const alternarPersona = (userId: string) => {
    const nuevos = idsResponsables.includes(userId)
      ? idsResponsables.filter((i) => i !== userId)
      : [...idsResponsables, userId];
    guardar({ responsables: nuevos });
  };

  return (
    <aside className="panel-tarea" aria-label={`Tarea: ${tarea.titulo}`}>
      <header className="panel-tarea-cabeza">
        <button
          type="button"
          className={`marca-hecha ${hecha ? 'hecha' : ''}`}
          aria-pressed={hecha}
          onClick={() => guardar({ estado: hecha ? 'TODO' : 'HECHA' })}
        >
          {t(idioma, hecha ? 'tarea.completada' : 'tarea.marcarCompletada')}
        </button>
        <button type="button" className="secundario" onClick={onCerrar} aria-label={t(idioma, 'tarea.cerrar')}>
          <Icono nombre="cerrar" />
        </button>
      </header>

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      <input
        className="panel-tarea-titulo"
        aria-label={t(idioma, 'tarea.titulo')}
        defaultValue={tarea.titulo}
        key={tarea.id}
        onBlur={(e) => {
          const v = e.target.value.trim();
          if (v && v !== tarea.titulo) guardar({ titulo: v });
        }}
      />
      <p className="suave pequeno">{tarea.proyectoNombre}</p>

      <dl className="panel-tarea-campos">
        <dt>{t(idioma, 'tarea.responsables')}</dt>
        <dd>
          <div className="fila-flex">
            {responsables.map((p) => (
              <button
                type="button"
                key={p.userId}
                className="ficha-persona"
                title={t(idioma, 'tarea.quitarA', { nombre: p.nombre })}
                onClick={() => alternarPersona(p.userId)}
              >
                <span className="ficha-avatar" aria-hidden="true">
                  {iniciales(p.nombre)}
                </span>
                {p.nombre}
                <span aria-hidden="true">✕</span>
              </button>
            ))}
            <button
              type="button"
              className="secundario"
              aria-expanded={eligiendoGente}
              onClick={() => setEligiendoGente((v) => !v)}
            >
              {responsables.length === 0 ? t(idioma, 'tarea.asignar') : '+'}
            </button>
          </div>
          {eligiendoGente && (
            <ul className="lista-elegir">
              {equipo.map((u) => (
                <li key={u.id}>
                  <button
                    type="button"
                    className="enlace"
                    aria-pressed={idsResponsables.includes(u.id)}
                    onClick={() => alternarPersona(u.id)}
                  >
                    {idsResponsables.includes(u.id) ? '✓ ' : ''}
                    {u.nombre}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </dd>

        <dt>{t(idioma, 'tarea.fecha')}</dt>
        <dd>
          <input
            type="date"
            aria-label={t(idioma, 'tarea.fecha')}
            value={tarea.vence ?? ''}
            onChange={(e) => guardar({ vence: e.target.value === '' ? null : e.target.value })}
          />
        </dd>

        <dt>{t(idioma, 'tarea.prioridad')}</dt>
        <dd>
          <select
            aria-label={t(idioma, 'tarea.prioridad')}
            value={tarea.prioridad}
            onChange={(e) =>
              guardar({ prioridad: e.target.value as 'ALTA' | 'MEDIA' | 'BAJA' })
            }
          >
            <option value="ALTA">{t(idioma, 'tarea.prioALTA')}</option>
            <option value="MEDIA">{t(idioma, 'tarea.prioMEDIA')}</option>
            <option value="BAJA">{t(idioma, 'tarea.prioBAJA')}</option>
          </select>
        </dd>

        <dt>{t(idioma, 'tarea.etiquetas')}</dt>
        <dd>
          <div className="fila-flex">
            {tarea.etiquetas.map((e) => (
              <button
                type="button"
                key={e.id}
                className="pastilla neutra pastilla-pulsable"
                title={`Quitar «${e.nombre}»`}
                onClick={() => {
                  void api
                    .proyQuitarEtiqueta(tarea.id, e.id)
                    .then(({ tarea: actualizada }) => {
                      setTarea((p) => (p ? { ...p, ...actualizada } : p));
                      onCambio(actualizada);
                    })
                    .catch(() => setError(t(idioma, 'tarea.noSeQuitoEtiqueta')));
                }}
              >
                {e.nombre} ✕
              </button>
            ))}
            <input
              type="text"
              className="entrada-etiqueta"
              placeholder={t(idioma, 'tarea.nuevaEtiqueta')}
              aria-label={t(idioma, 'tarea.anadirEtiqueta')}
              value={nuevaEtiqueta}
              maxLength={60}
              onChange={(e) => setNuevaEtiqueta(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== 'Enter' || nuevaEtiqueta.trim().length === 0) return;
                void api
                  .proyEtiquetarTarea(tarea.id, nuevaEtiqueta.trim())
                  .then(({ tarea: actualizada }) => {
                    setTarea((p) => (p ? { ...p, ...actualizada } : p));
                    onCambio(actualizada);
                    setNuevaEtiqueta('');
                  })
                  .catch(() => setError(t(idioma, 'tarea.noSePusoEtiqueta')));
              }}
            />
          </div>
        </dd>
      </dl>

      <div className="campo">
        <label htmlFor="tarea-desc">{t(idioma, 'tarea.descripcion')}</label>
        <textarea
          id="tarea-desc"
          rows={4}
          defaultValue={tarea.descripcion ?? ''}
          onBlur={(e) => {
            const v = e.target.value;
            if (v !== (tarea.descripcion ?? '')) guardar({ descripcion: v === '' ? null : v });
          }}
        />
      </div>

      {/*
        UNA IMAGEN DICE MAS QUE EL COMENTARIO QUE LA EXPLICA. La foto del cuadro
        electrico, del azulejo roto o de la pieza que hay que pedir ahorra tres
        mensajes de ida y vuelta, y queda con quien la subio y cuando.
      */}
      <GaleriaFotos
        idioma={idioma}
        entidad="TAREA"
        entidadId={tarea.id}
        puedeGestionar
        titulo={t(idioma, 'fotos.tarea')}
      />

      {/* --- Subtareas --------------------------------------------------- */}
      <section className="panel-tarea-bloque">
        <h3>
          {t(idioma, 'tarea.subtareas')}{' '}
          {tarea.hijas.length > 0 && (
            <span className="suave pequeno numerico">
              {tarea.hijas.filter((h) => h.estado === 'HECHA').length}/{tarea.hijas.length}
            </span>
          )}
        </h3>
        <ul className="lista-subtareas">
          {tarea.hijas.map((h) => (
            <li key={h.id} className={h.estado === 'HECHA' ? 'hecha' : undefined}>
              <button
                type="button"
                className={`circulo ${h.estado === 'HECHA' ? 'hecha' : ''}`}
                aria-label={t(
                  idioma,
                  h.estado === 'HECHA' ? 'tarea.reabrir' : 'tarea.completar',
                  { titulo: h.titulo },
                )}
                aria-pressed={h.estado === 'HECHA'}
                onClick={() => {
                  void api
                    .proyEditarTarea(h.id, { estado: h.estado === 'HECHA' ? 'TODO' : 'HECHA' })
                    .then(cargar)
                    .catch(() => setError(t(idioma, 'tarea.noSeCambioSubtarea')));
                }}
              >
                {h.estado === 'HECHA' ? '✓' : ''}
              </button>
              <span>{h.titulo}</span>
              {h.responsables[0] && (
                <span className="suave pequeno">{h.responsables[0].nombre}</span>
              )}
            </li>
          ))}
        </ul>
        <input
          type="text"
          placeholder={t(idioma, 'tarea.anadirSubtarea')}
          aria-label={t(idioma, 'tarea.anadirSubtarea')}
          value={nuevaSub}
          onChange={(e) => setNuevaSub(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== 'Enter' || nuevaSub.trim().length === 0) return;
            void api
              .proyCrearTarea(tarea.projectId, {
                titulo: nuevaSub.trim(),
                parentTaskId: tarea.id,
                sectionId: tarea.sectionId,
              })
              .then(() => {
                setNuevaSub('');
                return cargar();
              })
              .catch(() => setError(t(idioma, 'tarea.noSeCreoSubtarea')));
          }}
        />
      </section>

      {/* --- Conversación ------------------------------------------------- */}
      <section className="panel-tarea-bloque">
        <h3>{t(idioma, 'tarea.comentarios')}</h3>
        {tarea.conversacion.length === 0 && (
          <p className="suave pequeno">{t(idioma, 'tarea.sinComentarios')}</p>
        )}
        <ul className="lista-comentarios">
          {tarea.conversacion.map((c) => (
            <li key={c.id}>
              <span className="ficha-avatar" aria-hidden="true">
                {iniciales(c.autorNombre ?? '?')}
              </span>
              <div>
                <p className="pequeno">
                  <b>{c.autorNombre ?? t(idioma, 'tarea.alguien')}</b>{' '}
                  <span className="suave">{fechaLarga(c.creadoEn, idioma)}</span>
                </p>
                <p>{c.texto}</p>
              </div>
            </li>
          ))}
        </ul>
        <div className="fila-flex">
          <input
            type="text"
            placeholder={t(idioma, 'tarea.escribeComentario')}
            aria-label={t(idioma, 'tarea.escribeComentario')}
            value={comentario}
            maxLength={4000}
            style={{ flex: '1 1 10rem' }}
            onChange={(e) => setComentario(e.target.value)}
          />
          <button
            type="button"
            disabled={comentario.trim().length === 0}
            onClick={() => {
              void api
                .proyComentarTarea(tarea.id, comentario.trim())
                .then(() => {
                  setComentario('');
                  return cargar();
                })
                .catch(() => setError(t(idioma, 'tarea.noSePublico')));
            }}
          >
            {t(idioma, 'inc.enviar')}
          </button>
        </div>
      </section>
    </aside>
  );
}
